import { t as tr } from './i18n';
import { intlLocale } from './i18n';
/**
 * Pachetele și accesul firmei. Prețul e pe firmă, nu pe om. Diferă utilizatorii,
 * șantierele active și aprobarea comenzilor (de la Echipă în sus); restul funcțiilor
 * sunt în fiecare pachet, diferă doar câți utilizatori și câte șantiere active.
 *
 * Accesul are patru stări:
 * - `legacy`: firmă de dinainte de pachete (fără probă, fără pachet): fără limite;
 * - `trial`: o probă dată de mână din Admin, cu limitele pachetului Echipă;
 * - `active`: are pachet, pus de administrator după plată. „Plătit până la”
 *   rămâne doar evidență: accesul nu se oprește singur la scadență;
 * - `readonly`: fără pachet (firmă nouă, fără probă, sau proba s-a terminat):
 *   vede tot, nu schimbă nimic. Firmele noi pornesc așa (2026-10-12: fără probă
 *   de 14 zile; demo cu proprietarul, apoi pachetul).
 */

export type PlanId = 'start' | 'team' | 'company';
export type Billing = 'monthly' | 'yearly';

export interface Plan {
  id: PlanId;
  name: string;
  /** lei pe lună, fără TVA, la plata lunară */
  monthly: number;
  /** lei pe an, fără TVA: 10 luni, adică 2 luni gratuite */
  yearly: number;
  /** null = nelimitat */
  users: number | null;
  sites: number | null;
  support: string;
  /** aprobarea comenzilor: fără, un prag, pași pe valoare și pe șantier */
  approvals: 'none' | 'single' | 'multi';
  /** cererile pleacă din Procumi, cu mementouri; altfel omul copiază mailul */
  autoSend: boolean;
  /** în euro, fără TVA, pentru firmele din afara României (ca pe procumi.com/en) */
  eur: { monthly: number; yearly: number };
  /** prețul e „de la”: cel final se stabilește după demo, după mărimea firmei (2026-10-17) */
  quote?: boolean;
  /**
   * câte documente citește AI-ul pe lună (Atlas și citirile de mână), ca un client foarte
   * activ pe un pachet mic să nu coste mai mult decât plătește (2026-10-28); null = fără limită
   */
  aiMonthly: number | null;
}

export const PLANS: readonly Plan[] = [
  {
    id: 'start',
    get name() {
      return tr('Start');
    },
    monthly: 349,
    yearly: 3490,
    eur: { monthly: 89, yearly: 890 },
    users: 3,
    sites: 3,
    get support() {
      return tr('pe email');
    },
    approvals: 'none',
    autoSend: false,
    aiMonthly: 150,
  },
  {
    id: 'team',
    get name() {
      return tr('Echipă');
    },
    monthly: 749,
    yearly: 7490,
    eur: { monthly: 199, yearly: 1990 },
    users: 10,
    sites: null,
    get support() {
      return tr('pe email');
    },
    approvals: 'single',
    autoSend: true,
    aiMonthly: 600,
  },
  {
    id: 'company',
    get name() {
      return tr('Companie');
    },
    monthly: 1499,
    yearly: 14990,
    eur: { monthly: 399, yearly: 3990 },
    users: null,
    sites: null,
    get support() {
      return tr('prioritar, cu ajutor la pornire');
    },
    approvals: 'multi',
    autoSend: true,
    quote: true,
    aiMonthly: null,
  },
];

/** Câte zile are proba gratuită a unei firme noi (din 2026-10-27, la înscriere). */
export const TRIAL_DAYS = 14;

/**
 * Versiunea termenilor și a politicii de confidențialitate acceptate la înscriere: data de pe
 * procumi.com/termeni. Se schimbă odată cu textul de pe site (2026-11-03).
 */
export const TERMS_VERSION = '2026-10-27';

/**
 * Câte documente citește AI-ul (oferte, liste de cantități) cât ține proba. Fiecare
 * citire costă, iar proba e gratuită: peste limită prețurile se scriu de mână.
 * Pachetele plătite și firmele de dinainte nu au limită.
 */
export const TRIAL_AI_READS = 25;

/**
 * Fără pachet plătit, AI-ul citește doar în primele `TRIAL_DAYS` zile de la înscriere,
 * chiar dacă proba e prelungită din Administrare (2026-10-27, decizia proprietarului).
 */
export const AI_TRIAL_OVER_MESSAGE = tr(
  'Citirea automată cu AI e gratuită doar în primele {0} zile. Poți scrie prețurile de mână sau poți alege un pachet.',
  TRIAL_DAYS,
);

/** Luna în care se numără citirile, după ora României: '2026-10'. */
export function aiMonthKey(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest', year: 'numeric', month: '2-digit' })
    .format(now)
    .slice(0, 7);
}

/** „Pachetul Start citește automat 150 de documente pe lună…” */
export function aiMonthlyMessage(plan: Plan): string {
  const next = nextPlan(plan.id);
  return tr(
    'Pachetul {0} citește automat {1} de documente pe lună și le-ai folosit pe toate. Luna viitoare citirea pornește din nou; până atunci scrie prețurile de mână{2}.',
    plan.name,
    plan.aiMonthly,
    next ? tr(' sau treci la {0}', next.name) : '',
  );
}

export const AI_LIMIT_MESSAGE = tr(
  'În perioada de probă, Procumi citește automat {0} de documente și le-ai folosit pe toate. Poți scrie prețurile de mână sau poți alege un pachet.',
  TRIAL_AI_READS,
);

/** Limitele din probă: ale pachetului pe care îl recomandăm. */
export const TRIAL_PLAN: PlanId = 'team';

export const isPlanId = (v: string): v is PlanId => PLANS.some((p) => p.id === v);
export const isBilling = (v: string): v is Billing => v === 'monthly' || v === 'yearly';

export function planOf(id: string | null | undefined): Plan | null {
  return PLANS.find((p) => p.id === id) ?? null;
}

/**
 * Cât plătește firma pe perioadă: în lei pentru România, în euro pentru rest,
 * ca pe site. `amount` e în unități întregi (lei / euro), fără TVA.
 */
export function planPrice(
  plan: Plan,
  billing: Billing,
  currency: string,
): { amount: number; currency: 'RON' | 'EUR' } {
  return currency === 'RON'
    ? { amount: plan[billing], currency: 'RON' }
    : { amount: plan.eur[billing], currency: 'EUR' };
}

/** „349 lei” sau „€89” */
export const planMoney = (p: { amount: number; currency: 'RON' | 'EUR' }): string =>
  p.currency === 'RON'
    ? tr('{0} lei', p.amount.toLocaleString(intlLocale()))
    : `€${p.amount.toLocaleString(intlLocale())}`;

/** Se poate plăti singur, cu cardul: Start și Echipă. Companie are preț „de la”, deci trece pe la vânzări. */
export const selfServe = (plan: Plan): boolean => !plan.quote;

export const billingText = (b: string | null | undefined): string =>
  b === 'yearly' ? 'anual' : b === 'monthly' ? 'lunar' : '';

export type AccessState = 'legacy' | 'trial' | 'active' | 'readonly';

export interface Access {
  state: AccessState;
  /** pachetul ale cărui limite se aplică; null = fără limite (legacy) */
  plan: Plan | null;
  /** câte zile din probă mai are, cu ziua de azi; null în afara probei */
  trialDaysLeft: number | null;
  /** poate crea și modifica */
  writable: boolean;
}

const DAY = 86_400_000;

export function accessOf(
  c: { plan: string | null; trialEndsAt: string | null },
  now: Date = new Date(),
): Access {
  const plan = planOf(c.plan);
  if (plan) return { state: 'active', plan, trialDaysLeft: null, writable: true };
  if (!c.trialEndsAt) return { state: 'legacy', plan: null, trialDaysLeft: null, writable: true };
  const left = Date.parse(c.trialEndsAt) - now.getTime();
  if (left > 0) {
    return {
      state: 'trial',
      plan: planOf(TRIAL_PLAN),
      trialDaysLeft: Math.ceil(left / DAY),
      writable: true,
    };
  }
  return { state: 'readonly', plan: null, trialDaysLeft: null, writable: false };
}

/**
 * Atlas (agentul AI) e doar în pachetele plătite (proprietarul, 2026-10-28): în probă
 * ar citi documente pe banii noștri fără să aducă bani. Firmele de dinainte de pachete îl au.
 */
export const agentAvailable = (a: Access): boolean => a.state === 'active' || a.state === 'legacy';

export const AGENT_PAID_MESSAGE = 'Atlas e inclus în pachetele plătite. Alege un pachet ca să-l pornești.';

export const READONLY_MESSAGE =
  'Contul nu are încă un pachet. Poți vedea tot, dar ca să creezi sau să schimbi ceva alege un pachet.';

/** Pachetul următor, cu loc pentru mai mult; null dacă e deja cel mai mare. */
export function nextPlan(id: PlanId): Plan | null {
  const i = PLANS.findIndex((p) => p.id === id);
  return PLANS[i + 1] ?? null;
}

/** „Pachetul Start are 3 utilizatori. Ca să inviți mai mulți colegi, treci la Echipă.” */
export function limitMessage(kind: 'users' | 'sites', access: Access): string {
  const plan = access.plan!;
  const up = nextPlan(plan.id);
  const trial = access.state === 'trial';
  // fraze întregi, ca traducerea să le poată așeza cum îi e locul
  const what =
    kind === 'users'
      ? trial
        ? tr('În perioada de probă ai {0} utilizatori.', plan.users)
        : tr('Pachetul {0} are {1} utilizatori.', plan.name, plan.users)
      : trial
        ? tr('În perioada de probă ai {0} șantiere active.', plan.sites)
        : tr('Pachetul {0} are {1} șantiere active.', plan.name, plan.sites);
  const how =
    kind === 'users'
      ? up
        ? tr('Ca să inviți mai mulți colegi, treci la {0}.', up.name)
        : tr('Ca să inviți mai mulți colegi, scrie-ne.')
      : up
        ? tr('Ca să deschizi încă unul, închide un șantier terminat sau treci la {0}.', up.name)
        : tr('Ca să deschizi încă unul, închide un șantier terminat sau scrie-ne.');
  return `${what} ${how}`;
}

/** Mai încape încă unul? `used` = câți sunt acum. */
export function fits(limit: number | null, used: number): boolean {
  return limit == null || used < limit;
}

/**
 * Trimiterea cererilor din Procumi (cu mementouri și, de la etapa 3, linkul de
 * ofertă): în Echipă și Companie, în probă și la firmele de dinainte de pachete.
 */
export function canAutoSend(access: Access): boolean {
  if (!access.writable) return false;
  return access.plan ? access.plan.autoSend : true;
}

export const AUTO_SEND_MESSAGE =
  'Trimiterea din Procumi e în pachetele Echipă și Companie. În Start copiezi mailul și îl trimiți din programul tău.';

/** Pachetul a fost micșorat și firma folosește mai mult decât are: totul stă până alege administratorul. */
export const OVER_LIMIT_MESSAGE =
  'Firma are mai mulți colegi sau șantiere deschise decât permite pachetul. Administratorul contului trebuie să aleagă ce rămâne activ.';
